import React, { useState, useEffect } from 'react';
import { FiImage, FiX, FiUpload } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Editor, BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough, BtnNumberedList, BtnBulletList, BtnLink, BtnClearFormatting, HtmlButton, Separator, BtnStyles, Toolbar, EditorProvider } from 'react-simple-wysiwyg';

const FONT_OPTIONS = [
  { label: 'Font', value: '' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Calibri', value: 'Calibri' },
  { label: 'Cambria', value: 'Cambria' },
  { label: 'Candara', value: 'Candara' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'Impact', value: 'Impact' },
  { label: 'Tahoma', value: 'Tahoma' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS' },
  { label: 'Verdana', value: 'Verdana' },
];

const FontSelect = () => {
  const [font, setFont] = React.useState('');
  const savedRange = React.useRef(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const applyFont = (selected) => {
    if (selected && savedRange.current && !savedRange.current.collapsed) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('fontName', false, selected);
      savedRange.current = null;
    }
    setFont('');
  };

  return (
    <select
      value={font}
      onMouseDown={saveSelection}
      onChange={(e) => applyFont(e.target.value)}
      className="rsw-btn"
      title="Font family"
      style={{ padding: '0 6px', margin: '0 2px', height: '28px', fontSize: '13px' }}
    >
      {FONT_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
};

const ColorPicker = () => {
  const savedRange = React.useRef(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const applyColor = (color) => {
    if (savedRange.current && !savedRange.current.collapsed) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
      document.execCommand('styleWithCSS', false, true);
      document.execCommand('foreColor', false, color);
      savedRange.current = null;
    }
  };

  return (
    <input
      type="color"
      onClick={saveSelection}
      onChange={(e) => applyColor(e.target.value)}
      className="rsw-btn"
      title="Text color"
      style={{ padding: '0', margin: '0 2px', width: '28px', height: '28px', border: 'none', cursor: 'pointer' }}
    />
  );
};

const TemplateEditor = ({ template, onUpdate, onClose }) => {
  const [sections, setSections] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (template) {
      parseTemplate(template);
    }
  }, [template]);

  const parseTemplate = (template) => {
    // If the template JSON already defines editable sections, use them as-is.
    // This lets structured templates (like vardaan_uk_letter) expose multiple
    // textarea sections (subject, intro, practice items, body) while keeping
    // structural HTML (header, logos, footer) fixed.
    if (template.sections && Array.isArray(template.sections) && template.sections.length > 0) {
      console.log('Using template.sections directly:', template.sections.length, 'sections');
      setSections(template.sections);
      return;
    }

    const html = template.content || '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const extractedSections = [];

    console.log('Parsing template, HTML length:', html.length);
    console.log('Template has {{body}} placeholder:', html.includes('{{body}}'));
    
    // Extract header section
    const header = doc.querySelector('.header, header');
    if (header) {
      const headerText = header.textContent.trim();
      extractedSections.push({
        id: 'header',
        type: 'header',
        label: 'Header Title',
        value: headerText.replace(/[🎉📰📄]/g, '').trim(),
        html: header.innerHTML
      });
    }

    // Extract greeting
    const greeting = doc.querySelector('h2, p');
    if (greeting && (greeting.textContent.includes('Hello') || greeting.textContent.includes('Dear'))) {
      // Keep {{recipientName}} placeholder visible
      const greetingText = greeting.textContent.trim();
      extractedSections.push({
        id: 'greeting',
        type: 'greeting',
        label: 'Greeting',
        value: greetingText.includes('{{recipientName}}') ? greetingText : greetingText.replace(/\{\{recipientName\}\}/g, '{{recipientName}}'),
        html: greeting.outerHTML,
        hasPlaceholder: greetingText.includes('{{recipientName}}')
      });
    }

    // Extract body content - look for {{body}} placeholder OR extract from content area
    const bodyPlaceholder = html.includes('{{body}}');
    let bodyContent = '';

    if (bodyPlaceholder) {
      // Template already has {{body}} placeholder — extract the text that was stored
      // alongside it in the saved sections (if any)
      const storedBody = (template.sections || []).find(s => s.id === 'body' && s.type === 'textarea');
      bodyContent = storedBody?.value || '';
    } else {
      // No {{body}} placeholder — extract ALL body text by cloning the content div
      // and stripping out every structural element (heading, button, footer, images).
      // This captures both <p>-wrapped text AND bare text nodes.
      const contentDiv = doc.querySelector('.content, .body, [class*="content"], [class*="body"]');
      const rootEl = contentDiv || doc.querySelector('body');

      if (rootEl) {
        const clone = rootEl.cloneNode(true);

        // Remove headings (header title + greeting)
        clone.querySelectorAll('h1, h2, h3').forEach(el => el.remove());

        // Remove CTA buttons and their wrapper <p> when it only contains the button
        clone.querySelectorAll('a.button, [class*="button"]').forEach(el => {
          const parent = el.parentElement;
          if (parent && parent.tagName === 'P' && parent.children.length === 1) {
            parent.remove();
          } else {
            el.remove();
          }
        });

        // Remove footer / unsubscribe block
        clone.querySelectorAll('.footer, footer').forEach(el => el.remove());

        // Remove image-only <p> tags (images are handled as separate sections)
        clone.querySelectorAll('p').forEach(p => {
          const hasOnlyImg = p.children.length > 0 && p.querySelectorAll('img').length === p.children.length && p.textContent.trim() === '';
          if (hasOnlyImg) p.remove();
        });

        // Get full text, line-by-line, filtering out structural boilerplate lines
        const SKIP = /^(Hello|Dear|Best regards|Looking forward|Claim Your Offer|Unsubscribe|©|You received this email|Special Offer)/i;
        bodyContent = clone.textContent
          .split('\n')
          .map(l => l.trim())
          .filter(l => l.length > 0 && !SKIP.test(l))
          .join('\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
      }
    }
    
    // Always add body section (required for email content)
    // If no content found, provide empty string so user can add their own
    extractedSections.push({
      id: 'body',
      type: 'textarea',
      label: 'Email Content',
      value: bodyContent || '',
      placeholder: 'Enter your email content here...',
      html: bodyPlaceholder ? '{{body}}' : '{{body}}'
    });

    // Extract button/CTA
    const button = doc.querySelector('.button, a.button, button');
    if (button) {
      extractedSections.push({
        id: 'button',
        type: 'button',
        label: 'Call-to-Action Button',
        text: button.textContent.trim(),
        url: button.href || '#',
        html: button.outerHTML
      });
    }

    // Extract footer
    const footer = doc.querySelector('.footer, footer');
    if (footer) {
      extractedSections.push({
        id: 'footer',
        type: 'text',
        label: 'Footer Text',
        value: footer.textContent.trim(),
        html: footer.innerHTML
      });
    }

    // Only extract images for templates that don't have a {{body}} placeholder.
    // Templates with {{body}} (like vardaan_uk_letter) have fixed structural logos
    // that should not be user-editable — they come from the server-side template HTML.
    if (!bodyPlaceholder) {
      const images = doc.querySelectorAll('img');
      const seenImageSrcs = new Set();
      images.forEach((img, index) => {
        const imgSrc = img.src || img.getAttribute('src') || '';
        if (!imgSrc || seenImageSrcs.has(imgSrc) || imgSrc.includes('tracking/open')) {
          return;
        }
        seenImageSrcs.add(imgSrc);
        const filename = imgSrc.split('/').pop() || `image-${index}`;
        extractedSections.push({
          id: `image-${filename.split('.')[0]}`,
          type: 'image',
          label: `Image ${seenImageSrcs.size}`,
          src: imgSrc,
          alt: img.alt || '',
          html: img.outerHTML
        });
      });
    }

    setSections(extractedSections);
  };

  const handleSectionChange = (id, field, value) => {
    setSections(sections.map(section => {
      if (section.id === id) {
        return { ...section, [field]: value };
      }
      return section;
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post('/api/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url;
      setUploadedImages([...uploadedImages, imageUrl]);
      
      // Add image section
      const newSection = {
        id: `image-${Date.now()}`,
        type: 'image',
        label: `Image ${uploadedImages.length + 1}`,
        src: imageUrl,
        alt: file.name,
        html: `<img src="${imageUrl}" alt="${file.name}" style="max-width: 100%; height: auto;" />`
      };
      
      setSections([...sections, newSection]);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Error uploading image: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const generateHTML = () => {
    return generateHTMLWithSections(sections);
  };

  const generateHTMLWithSections = (sectionsToUse) => {
    let html = template.content || '';
    
    console.log('=== STARTING HTML GENERATION ===');
    console.log('Template content length:', html.length);
    console.log('Template contains {{body}}:', html.includes('{{body}}'));
    console.log('Sections to process:', sectionsToUse.length);

    // First, handle body replacement separately to ensure it works
    const bodySection = sectionsToUse.find(s => s.id === 'body' && s.type === 'textarea');

    // ── Normalize: inject {{body}} placeholder if the template HTML doesn't have one ──
    // This happens when templates were saved with hardcoded body text instead of a placeholder.
    if (!html.includes('{{body}}') && bodySection) {
      const hasButton = /<a\b[^>]*\bclass="button"/i.test(html);
      if (html.includes('</h2>') && hasButton) {
        // Replace everything between </h2> and the CTA button with {{body}}
        html = html.replace(
          /(<\/h2>)([\s\S]*?)(<a\b[^>]*\bclass="button")/i,
          '$1\n{{body}}\n$3'
        );
      } else if (html.includes('</h2>')) {
        html = html.replace('</h2>', '</h2>\n{{body}}');
      } else {
        // Last resort: append {{body}} before </body>
        html = html.replace('</body>', '{{body}}\n</body>');
      }
      console.log('✅ Injected {{body}} placeholder into template (was missing)');
    }
    console.log('Looking for body section...', bodySection ? 'FOUND' : 'NOT FOUND');
    
    if (bodySection) {
      console.log('Body section details:', {
        id: bodySection.id,
        type: bodySection.type,
        valueLength: (bodySection.value || '').length,
        valuePreview: (bodySection.value || '').substring(0, 50)
      });
    }
    
    // NOTE: We intentionally KEEP {{body}} in the saved template HTML.
    // The actual body text is stored in sections[body].value.
    // When the server processes the email, it calls processTemplate(content, { body: bodyValue })
    // which replaces {{body}} at send/preview time. This allows AI-rephrased content and
    // per-recipient customisations to be injected without re-saving the template to disk each time.
    if (bodySection) {
      console.log('✅ Body section found — keeping {{body}} placeholder in template HTML. Value:', (bodySection.value || '').substring(0, 60));
    } else {
      console.warn('⚠️ Body section not found in sections list.');
    }
    
    // First, collect all image sections to handle them together (prevent duplicates)
    const imageSections = sectionsToUse.filter(s => s.type === 'image' && s.src && s.html);
    const processedImageSrcs = new Set();
    
    // Process non-image sections first
    sectionsToUse.forEach(section => {
      if (section.type === 'image') return; // Skip images for now
      
      console.log('Processing section:', section.id, 'type:', section.type, 'value length:', (section.value || '').length);
      
      switch (section.type) {
        case 'header':
          // Replace header in .header div or header tag
          // Clean the header value (remove emojis and trim)
          const cleanHeaderValue = (section.value || '').replace(/[🎉📰📄]/g, '').trim();
          
          // Try multiple patterns to catch all variations
          // Pattern 1: Simple h1 replacement
          html = html.replace(/<h1[^>]*>.*?<\/h1>/i, `<h1>${cleanHeaderValue}</h1>`);
          
          // Pattern 2: Replace within .header div (more specific)
          html = html.replace(/(<div[^>]*class=["']header["'][^>]*>[\s\S]*?<h1[^>]*>)(.*?)(<\/h1>)/i, 
            `$1${cleanHeaderValue}$3`);
          
          // Pattern 3: Replace any h1 inside a div with class header
          html = html.replace(/(<div[^>]*class=["'][^"']*header[^"']*["'][^>]*>[\s\S]*?<h1[^>]*>)(.*?)(<\/h1>)/i, 
            `$1${cleanHeaderValue}$3`);
          
          console.log('Header replacement - value:', cleanHeaderValue, 'HTML contains h1:', html.includes('<h1>'));
          break;
        case 'greeting':
          // Preserve the greeting format with {{recipientName}} placeholder
          const greetingValue = section.value || 'Hello {{recipientName}},';
          html = html.replace(/<h2[^>]*>.*?<\/h2>/i, `<h2>${greetingValue}</h2>`);
          // Also replace in <p> tags if present
          html = html.replace(/<p[^>]*>.*?Dear.*?<\/p>/i, `<p>${greetingValue}</p>`);
          break;
        case 'body':
        case 'textarea':
          // Keep all {{placeholder}} tokens in the saved template HTML.
          // Server-side processTemplate replaces them at send/preview time using
          // dynamic variables and template section values. No replacement here.
          break;
        case 'button':
          html = html.replace(/<a[^>]*class="button"[^>]*>.*?<\/a>/i, 
            `<a href="${section.url || '#'}" class="button">${section.text || 'Click Here'}</a>`);
          break;
        default:
          // Unknown section type, skip
          break;
      }
    });
    
    // Now handle images - remove ALL existing image tags (except tracking pixels) first, then insert all images once
    // Remove all image tags that are not tracking pixels
    html = html.replace(/<p[^>]*style=["'][^"']*text-align:\s*center[^"']*["'][^>]*>\s*<img[^>]*>\s*<\/p>/gi, '');
    html = html.replace(/<img[^>]*src=["'][^"']*\/api\/uploads\/images\/[^"']*["'][^>]*>/gi, '');
    
    // Also remove any standalone image tags in the content area
    const contentDivMatch = html.match(/(<div[^>]*class="content"[^>]*>)([\s\S]*?)(<\/div>)/i);
    if (contentDivMatch) {
      const contentDiv = contentDivMatch[2];
      const cleanedContent = contentDiv.replace(/<img[^>]*src=["'][^"']*\/api\/uploads\/images\/[^"']*["'][^>]*>/gi, '');
      html = html.replace(contentDivMatch[0], contentDivMatch[1] + cleanedContent + contentDivMatch[3]);
    }
    
    // Insert all images once, after greeting (h2) and before body content
    if (imageSections.length > 0) {
      // Deduplicate images by src
      const uniqueImages = [];
      const seenSrcs = new Set();
      imageSections.forEach(section => {
        if (section.src && !seenSrcs.has(section.src)) {
          seenSrcs.add(section.src);
          uniqueImages.push(section);
        }
      });
      
      const imagesHTML = uniqueImages.map(section => 
        `<p style="text-align: center; margin: 20px 0;">${section.html}</p>`
      ).join('\n');
      
      // Find the best insertion point - use a more specific replacement
      if (html.includes('</h2>') && html.includes('{{body}}')) {
        // Insert between h2 and {{body}} - replace only the first occurrence after h2
        const h2Index = html.indexOf('</h2>');
        const bodyIndex = html.indexOf('{{body}}');
        if (h2Index < bodyIndex) {
          html = html.substring(0, h2Index + 5) + '\n' + imagesHTML + '\n' + html.substring(h2Index + 5);
        }
      } else if (html.includes('</h2>')) {
        // Insert after h2 - replace only the first occurrence
        const h2Index = html.indexOf('</h2>');
        html = html.substring(0, h2Index + 5) + '\n' + imagesHTML + '\n' + html.substring(h2Index + 5);
      } else if (html.includes('{{body}}')) {
        // Insert before {{body}} placeholder
        html = html.replace('{{body}}', `${imagesHTML}\n{{body}}`);
      } else {
        // Insert at the beginning of content div
        html = html.replace(/(<div[^>]*class="content"[^>]*>)/i, `$1\n${imagesHTML}\n`);
      }
    }
    
    console.log('Final HTML length:', html.length);
    console.log('Final HTML contains {{body}}:', html.includes('{{body}}'));
    
    return html;
  };

  const handleSave = async () => {
    // Check if body section has content BEFORE generating HTML
    const bodySection = sections.find(s => s.id === 'body' && s.type === 'textarea');
    
    const bodyValue = bodySection?.value || '';
    
    const trimmedValue = bodyValue.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    
    if (trimmedValue === '') {
      toast.error('⚠️ Please fill in the "Email Content" section before saving. This is required!', {
        duration: 5000,
        style: {
          background: '#fee',
          color: '#c33',
          border: '1px solid #fcc'
        }
      });
      const bodyElement = document.querySelector(`[data-section-id="body"]`);
      if (bodyElement) bodyElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // Update sections with the DOM value to ensure we have the latest
    const updatedSections = sections.map(s => 
      s.id === 'body' && s.type === 'textarea' 
        ? { ...s, value: bodyValue }
        : s
    );
    
    // Use updated sections for HTML generation
    const updatedContent = generateHTMLWithSections(updatedSections);
    
    console.log('Generated HTML contains {{body}}:', updatedContent.includes('{{body}}'));
    console.log('Generated HTML length:', updatedContent.length);
    
    // {{body}} is intentionally kept in the saved HTML as a placeholder.
    // The body text lives in sections[body].value and is injected at send/preview time.
    // So we only block save if the body SECTION VALUE itself is empty.
    console.log('Generated HTML contains {{body}}:', updatedContent.includes('{{body}}'));
    console.log('Generated HTML length:', updatedContent.length);
    
    // Update state with the corrected sections
    setSections(updatedSections);
    
    const updatedTemplate = {
      ...template,
      content: updatedContent,
      sections: updatedSections
    };
    
    // Save to backend
    try {
      const userEmail = (localStorage.getItem('ms_user_email') || '').toLowerCase();
      await axios.post('/api/templates', { ...updatedTemplate, user: userEmail });
      onUpdate(updatedTemplate);
      toast.success('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      // Still update locally even if backend save fails
      onUpdate(updatedTemplate);
      toast.error('Template updated locally but failed to save to server');
    }
  };

  if (!template) return null;

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-purple-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-800">Edit Template: {template.name}</h3>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4 max-h-[70vh] overflow-y-auto mb-4">
        {sections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>Loading template sections...</p>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.id} className="border rounded-lg p-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {section.label}
              </label>
              
            {section.type === 'textarea' && (
              <div data-section-id={section.id}>
                {section.id === 'body' && (!section.value || section.value.trim() === '') && (
                  <p className="text-xs text-red-600 mb-1 font-semibold">
                    ⚠️ This field is required! Please add your email content here.
                  </p>
                )}
                <div className={`border-2 rounded-lg overflow-hidden ${
                  section.id === 'body' && (!section.value || section.value.trim() === '')
                    ? 'border-red-300'
                    : 'border-gray-300 focus-within:border-purple-500'
                }`}>
                  <EditorProvider>
                    <Editor
                      value={section.value || ''}
                      onChange={(e) => handleSectionChange(section.id, 'value', e.target.value)}
                      style={{ minHeight: '220px', fontSize: '13px', padding: '10px' }}
                      containerProps={{ style: { background: '#fff' } }}
                    >
                      <Toolbar>
                        <BtnBold />
                        <BtnItalic />
                        <BtnUnderline />
                        <BtnStrikeThrough />
                        <Separator />
                        <FontSelect />
                        <ColorPicker />
                        <Separator />
                        <BtnStyles />
                        <Separator />
                        <BtnNumberedList />
                        <BtnBulletList />
                        <Separator />
                        <BtnLink />
                        <Separator />
                        <BtnClearFormatting />
                        <HtmlButton />
                      </Toolbar>
                    </Editor>
                  </EditorProvider>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-xs text-gray-500 font-medium mr-1">Insert variable:</span>
                  {[
                    { label: '{{recipientName}}', title: 'Recipient Name' },
                    { label: '{{recipientEmail}}', title: 'Recipient Email' },
                    { label: '{{recipientTitle}}', title: 'Recipient Title' },
                    { label: '{{recipientLocation}}', title: 'Location' },
                    { label: '{{date}}', title: 'Date' },
                  ].map(v => (
                    <button
                      key={v.label}
                      type="button"
                      title={v.title}
                      onClick={() => handleSectionChange(section.id, 'value', (section.value || '') + v.label)}
                      className="px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 rounded text-xs font-mono hover:bg-purple-100 transition-colors"
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
              
              {section.type === 'text' && (
                <input
                  type="text"
                  value={section.value}
                  onChange={(e) => handleSectionChange(section.id, 'value', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:border-purple-500 focus:outline-none"
                />
              )}
              
              {section.type === 'greeting' && (
                <div>
                  <input
                    type="text"
                    value={section.value}
                    onChange={(e) => handleSectionChange(section.id, 'value', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:border-purple-500 focus:outline-none"
                    placeholder={'Hello {{recipientName}},'}
                  />
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800 font-semibold mb-1">
                      💡 Dynamic Name Replacement:
                    </p>
                    <p className="text-xs text-blue-700 mb-2">
                      The <code className="bg-blue-100 px-1 rounded font-mono">{'{{recipientName}}'}</code> placeholder will be automatically replaced with:
                    </p>
                    <ul className="text-xs text-blue-700 ml-4 list-disc space-y-1">
                      <li><strong>Priority 1:</strong> Name from <strong>"Name" column</strong> in uploaded Excel/CSV files</li>
                      <li><strong>Priority 2:</strong> Extracted from email address (e.g., <code className="bg-blue-100 px-1 rounded">john.doe@example.com</code> → <code className="bg-blue-100 px-1 rounded">John Doe</code>)</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2 italic">
                      Example: "Hello {'{{recipientName}}'}," becomes "Hello John Smith," when sent to john.smith@example.com
                    </p>
                  </div>
                </div>
              )}
              
              {section.type === 'button' && (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={section.text}
                    onChange={(e) => handleSectionChange(section.id, 'text', e.target.value)}
                    placeholder="Button Text"
                    className="w-full px-3 py-2 border rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                  <input
                    type="url"
                    value={section.url}
                    onChange={(e) => handleSectionChange(section.id, 'url', e.target.value)}
                    placeholder="Button URL"
                    className="w-full px-3 py-2 border rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                </div>
              )}
              
              {section.type === 'image' && (
                <div className="space-y-2">
                  {section.src ? (
                    <div className="relative">
                      <img src={section.src} alt={section.alt} className="max-w-full h-32 object-contain border rounded" />
                      <button
                        onClick={() => removeImage(section.id)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 transition-colors">
                      <FiUpload className="mr-2" />
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          ))
        )}

        {/* Add Image Button - only for non-structured templates */}
        {!(template.content || '').includes('{{body}}') && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mt-4">
            <label className="flex items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
              <FiImage className="mr-2 text-gray-400" />
              <span className="text-gray-600">Add Image to Template</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploadingImage}
              />
            </label>
            {uploadingImage && (
              <p className="text-sm text-gray-500 mt-2 text-center">Uploading...</p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={handleSave}
          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
        >
          Save Changes
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default TemplateEditor;
